import style from "@/styles/pages/AnswerSecurity/AnswerSecurity.module.css";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useEffect, useRef, useState } from "react";
import { setApplicantId, setCredentialsCreated, setDateOfBirthDay, setDateOfBirthMonth, setDateOfBirthYear, setEmailAddress, setExistingMemberFields, setFirstName, setInvalidCounter, setLastName, setMaritalStatus, setMiddleName, setNumOfDependents, setPhoneNumber, setPhoneType, setResendCounter, setResidentStatus, setSendFailCounter, setSin, setTitle, setTwilioResult, setTwilioResultCodeVerified } from "@/slices/applicantSlice";
import { RootState } from "@/store/dtm";
import AuthCode, { AuthCodeRef } from "react-auth-code";
import { AmendMemmberCoApplicantCommand, AnswerChallengeDto, ApplicantForManipulationDto, ApplicationDto, CreateTwilioCode, RememberClientDto, SendVerifyCodeDto, VerifyTwilioCode } from "@/lib/DTMWebAPIClient.generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "@/context/AuthContext";
import { setStreetNumber, setStreetName, setUnitNumber, setCity, setProvince, setPostalCode, setCountry, setStreetType, setUnitType, setAddressId, setStreetDirection } from "@/slices/homeAddressSlice";
import FadeTransition from "@/styles/animation/FadeTransition";
import { americanStatesAbbreviationList, processUserAppData } from "@/lib/util";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { motion } from "framer-motion";
import { setAMLFieldsCompleted } from "@/slices/legalTermsSlice";
import { streetTypes } from "@/ts/streetTypes";

export default function TwoFactorAuthentication() {
    const client = createApiClient({ timeout: 60000 });

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const getAmericanStates = americanStatesAbbreviationList();
    const { login } = useAuth();

    const [passwordShown, setPasswordShown] = useState(false);

    const [code, setCode] = useState("");
    const [codeIsValid, setCodeIsValid] = useState("");
    const [codeInputIndicator, setCodeInputIndicator] = useState(0);
    const [verifying, setVerifying] = useState(true);
    const [resendMessage, setResendMessage] = useState(false);
    const [verifyInProgress, setVerifyInProgress] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [lastFourNumber, setLastFourNumber] = useState("XXXX");
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [fetchingInfo, setFetchingInfo] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [isNewMember, setIsNewMember] = useState(false);
    const [invalidAccountType, setInvalidAccountType] = useState(0);
    const [applicantInfo, setApplicantInfo] = useState<ApplicantForManipulationDto | undefined>();
    const [docIqDestination, setDocIqDestination] = useState<string | undefined>(undefined);
    const authCodeRef = useRef<AuthCodeRef>(null);
    const currentIndex = useRef(0);

    const credential = useSelector(
        (state: RootState) => state.credential.credential
    );
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const twoFactorAuth = useSelector(
        (state: RootState) => state.twoFactorAuth
    );
    const phoneNumber = useSelector(
        (state: RootState) => state.applicant.applicant.phoneNumber
    );
    const sendFailCounter = useSelector(
        (state: RootState) => state.applicant.applicant.sendFailCounter
    );
    const resendCounter = useSelector(
        (state: RootState) => state.applicant.applicant.resendCounter
    );
    const invalidCounter = useSelector(
        (state: RootState) => state.applicant.applicant.invalidCounter
    );

    useEffect(() => {
        if (code.length === 6 && !verifyInProgress && currentIndex.current === 6) {
            setVerifyInProgress(true);
            verifyCode(phoneNumber, code);
        }
    }, [code]);

    useEffect(() => {
        authCodeRef.current?.getInput(0).value?.addEventListener('keydown', (e) => advanceCursor(e, 0));
        authCodeRef.current?.getInput(1).value?.addEventListener('keydown', (e) => advanceCursor(e, 1));
        authCodeRef.current?.getInput(2).value?.addEventListener('keydown', (e) => advanceCursor(e, 2));
        authCodeRef.current?.getInput(3).value?.addEventListener('keydown', (e) => advanceCursor(e, 3));
        authCodeRef.current?.getInput(4).value?.addEventListener('keydown', (e) => advanceCursor(e, 4));
        authCodeRef.current?.getInput(5).value?.addEventListener('keydown', (e) => advanceCursor(e, 5));
        authCodeRef.current?.getInput(0).value?.addEventListener('focus', (e) => clearFields());

        setLastFourNumber(twoFactorAuth.authTwoFa.challenge2FA?.phone ? twoFactorAuth.authTwoFa.challenge2FA?.phone.slice(-4) : "");
    }, []);

    const verificationHandler = (e: any) => {
        setCode(e);
    }

    const reSendCode = (phoneNumber: string, channel: string) => {
        setVerifyingCode(true);
        client.apiAuthOlbloginSendverifycode(new SendVerifyCodeDto({
            sessionId: twoFactorAuth.authTwoFa.sessionId,
            channel: channel
        })).then(function (data) {
            setVerifyingCode(false);
            if (data) {
                setVerifying(true);
                dispatch(setResendCounter(resendCounter + 1));
                setResendMessage(true);
                setCodeIsValid("");
                setCodeInputIndicator(0);
                setCode("");
                setCodeResent(true);
                setTimeout(() => {
                    setCodeResent(false);
                }, 2000)
            } else {
                dispatch(setSendFailCounter(sendFailCounter + 1));
            }
        }).catch(error => {
            setVerifyingCode(false);
            console.log("Failed to resend code");
        });
    }

    const verifyCode = (phoneNumber: string, code: string) => {
        if (verifying) {
            setResendMessage(false);
            setVerifyingCode(true);
            client.apiAuthOlbloginAnswerchallenge(new AnswerChallengeDto({
                username: credential.email,
                sessionId: twoFactorAuth.authTwoFa.sessionId,
                answer: code,
                dealId: application.id,
            })).then(function (result) {

                setTwilioResultCodeVerified(true);
                setCodeIsValid("true");
                setVerifying(false);
                setCodeInputIndicator(2);
                setVerifyingCode(false);

                if (result && result.applicant) {
                    setApplicantInfo(result.applicant);
                }

                if (result && result.docIqDestination) {
                    setDocIqDestination(result.docIqDestination);
                }

                dispatch(setCredentialsCreated(true));
                login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");

                // dispatch(setCredentialsCreated(true));
                // navigate("/single/select-application");

                //Check if this member's start date is less than 60 days; if so, redirect to error page
                if (result.applicant?.startDate?.year() && result.applicant.startDate.year() > 1901) {
                    let today = dayjs();
                    if (today.diff(result.applicant.startDate, 'day') < 60) {
                        setIsNewMember(true);
                    }
                }
            }).catch((error) => {
                setCodeIsValid("false");
                setTwilioResultCodeVerified(false);
                setVerifyInProgress(false);
                setVerifying(true);
                setVerifyingCode(false);
                setCodeInputIndicator(1);
                dispatch(setInvalidCounter(invalidCounter + 1));
                authCodeRef.current?.getInput(5).value?.blur();
                checkInvalidCounter();

                //Check if error message contains error code LU10057000; if so, redirect to login screen
                if (error.response && error.response.message && error.response.message.includes("LU10057000")) {
                    navigate("/login", { replace: true, state: { accountLocked: true } });
                }

                //If membership is < 60 days old or no email address exists, redirect to generic error page
                if (error.response.code) {
                    setCodeIsValid("true");
                    setCodeInputIndicator(2);
                    if (error.response.type == "NewMemberException") {
                        setInvalidAccountType(1);
                    } else if (error.response.type == "JointMemberException") {
                        setInvalidAccountType(2);
                    } else if (error.response.code == 1000) {
                        setInvalidAccountType(3);
                    }
                }
            });
        }
    }

    const advanceCursor = (ev: KeyboardEvent, index: number) => {
        currentIndex.current = index;
        let isNumber = /^[0-9]$/i.test(ev.key);
        if (isNumber) {
            currentIndex.current = index + 1;
            if (authCodeRef.current?.getInput(index).value?.value === ev.key) {
                authCodeRef.current?.getInput(index).value?.blur();

                //Delay advancing to next cursor to avoid skipping
                const timeout = setTimeout(() => {
                    authCodeRef.current?.getInput(index + 1).value?.focus();
                }, 50)
                return () => clearTimeout(timeout)
            }
        }
    }

    const clearFields = () => {
        setCodeInputIndicator(0);
        authCodeRef.current?.clear();
    }

    const checkInvalidCounter = () => {
        if (invalidCounter + 1 >= 3) {
            //navigate("/single/select-application");
        }
    }


    const handleClick = async () => {
        let authenticatedClient = createApiClient({ timeout: 60000, token: credential.accessToken });
        //Redirect to Error Page if Account is Invalid
        if (invalidAccountType == 1) {
            navigate("/error/1", { replace: true });
        }
        if (invalidAccountType == 2) {
            navigate("/login", { replace: true, state: { invalidMember: true } });
        }
        if (invalidAccountType == 3) {
            navigate("/error/9", { replace: true });
        }

        //Remember device if checked
        if (rememberDevice) {
            authenticatedClient.apiAuthOlbloginRememberclient(new RememberClientDto({
                sessionId: twoFactorAuth.authTwoFa.sessionId
            })).then(function (result) {
                Cookies.set(credential.email, result.clientToken ? result.clientToken : "");
            }).catch((error) => {
                console.log(error);
            });
        }
        //Proceed if code was successfully validated
        if (codeIsValid === "true") {
            if (isNewMember) {
                navigate("/error/1", { replace: true });
            } else {
                dispatch(setCredentialsCreated(true));

                //IMPORTANT: If the co-applicant is an existing member, replace the temporary applicant (that was added by the primary) with the one that
                //is already tied to the existing member
                if (applicant.id != 0) {
                    await authenticatedClient.apiDealsApplicantMemberAmend(new AmendMemmberCoApplicantCommand({
                        temporaryApplicantId: applicant.id,
                    })).then((res) => {
                        console.log(res);
                        // Handle response if needed
                    }).catch((error) => {
                        // Handle error if needed
                    });
                }

                if (applicantInfo) {
                    console.log(applicantInfo);
                    let dobMonth = applicantInfo.dateOfBirth.month() + 1;
                    let dobDay = applicantInfo.dateOfBirth.date();
                    dispatch(setApplicantId(applicantInfo.id ? applicantInfo.id : 0));
                    dispatch(setTitle(applicantInfo.title ? applicantInfo.title : ""));
                    dispatch(setFirstName(applicantInfo.firstName));
                    dispatch(setMiddleName(applicantInfo.middleName ? applicantInfo.middleName : ""));
                    dispatch(setLastName(applicantInfo.lastName));
                    dispatch(setPhoneNumber(applicantInfo.phoneNumber));
                    dispatch(setPhoneType(applicantInfo.phoneType ? applicantInfo.phoneType.replace(/\w/, p => p.toUpperCase()) : ""));
                    dispatch(setEmailAddress(applicantInfo.email));
                    dispatch(setDateOfBirthYear(applicantInfo.dateOfBirth.year() + ""));
                    dispatch(setDateOfBirthMonth(dobMonth.toString().length == 1 ?
                        "0" + dobMonth.toString()
                        : dobMonth.toString()));
                    dispatch(setDateOfBirthDay(dobDay + ""));
                    dispatch(setMaritalStatus(applicantInfo.maritalStatus ? applicantInfo.maritalStatus : ""));
                    dispatch(setNumOfDependents(applicantInfo.numberOfDependents ? applicantInfo.numberOfDependents : 0));

                    if (applicantInfo.address) {
                        dispatch(setAddressId(applicantInfo.address[0].id ? applicantInfo.address[0].id : 0));
                        dispatch(setStreetNumber(applicantInfo.address[0].streetNumber ? applicantInfo.address[0].streetNumber : ""));
                        dispatch(setStreetName(applicantInfo.address[0].streetName ? applicantInfo.address[0].streetName : ""));
                        dispatch(setStreetDirection(applicantInfo.address[0].streetDirection ? applicantInfo.address[0].streetDirection : ""));
                        dispatch(setUnitType(applicantInfo.address[0].unitType + ""));
                        dispatch(setUnitNumber(applicantInfo.address[0].unitNumber + ""));
                        dispatch(setCity(applicantInfo.address[0].city));
                        //If the country is set to United States but the province/state does not match a US state, do not set the province value as it is likely a Canadian province that was not properly cleared
                        if (applicantInfo.address[0].country == "United States" && !getAmericanStates.includes(applicantInfo.address[0].region ? applicantInfo.address[0].region : "")) {
                            dispatch(setProvince(""));
                        } else {
                            dispatch(setProvince(applicantInfo.address[0].region ? applicantInfo.address[0].region : ""));
                        }
                        dispatch(setPostalCode(applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode : ""));
                        dispatch(setCountry(applicantInfo.address[0].country ? applicantInfo.address[0].country : ""));
                        dispatch(setResidentStatus(applicantInfo.address[0].residenceStatusType_Name ? applicantInfo.address[0].residenceStatusType_Name : ""));

                        //Attempt to parse the street type based on whether it contains multiple words
                        if ((applicantInfo.address[0].streetType == undefined || applicantInfo.address[0].streetType == "") && applicantInfo.address[0].streetName && applicantInfo.address[0].streetName.split(" ").length > 1) {
                            let streetNameComponents = applicantInfo.address[0].streetName ? applicantInfo.address[0].streetName.split(" ") : [];
                            let newStreetName = "";
                            for (let i = 0; i < streetNameComponents.length - 1; i++) {
                                newStreetName += streetNameComponents[i] + " ";
                            }
                            let streetType = streetNameComponents[streetNameComponents.length - 1].toUpperCase();
                            dispatch(setStreetType(streetTypes.includes(streetType) ? streetType : ""));
                            dispatch(setStreetName(newStreetName.trim()));
                        } else if (applicantInfo.address[0].streetType != undefined && applicantInfo.address[0].streetType != "") {
                            dispatch(setStreetType(applicantInfo.address[0].streetType));
                        }
                    }

                    //Check Validity of Ovation Fields
                    let phoneNumberReg = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/);
                    dispatch(setExistingMemberFields({
                        isFirstNameAvailable: applicantInfo.firstName.trim() != '',
                        isLastNameAvailable: applicantInfo.lastName.trim() != '',
                        isPhoneNumberAvailable: phoneNumberReg.test(applicantInfo.phoneNumber),
                        isSinAvailable: true,
                        isProvinceAvailable: applicantInfo.address != undefined && applicantInfo.address[0].region ? applicantInfo.address[0].region.trim() != '' : false,
                        isPostalCodeAvailable: applicantInfo.address != undefined && applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode.trim() != '' : false,
                        isCountryAvailable: applicantInfo.address != undefined && applicantInfo.address[0].country.trim() != ''
                    }));
                }

                setFetchingInfo(true);
                authenticatedClient.apiApplicationsFetch().then(async (appData) => {
                    setFetchingInfo(false);
                    if (appData) {
                        //Prefil member info such as subject property, address, etc.
                        processUserAppData(appData, dispatch, false);

                        //TODO: If member attempts to sign in with a different invitiation code; redirect to login screen and display error message
                        dispatch(setAMLFieldsCompleted(true));

                        if (docIqDestination == undefined || docIqDestination == "") {
                            navigate("/home", { replace: true });
                            return;
                        } else {
                            window.location.replace(docIqDestination);
                        }

                    } else {
                        navigate("/confirm-consent", { replace: true });
                    }
                }).catch((error) => {
                    setFetchingInfo(false);
                    console.log("Error fetching application info", error);
                });
            }
        }
    }

    const togglePasswordVisiblity = () => {
        setPasswordShown(passwordShown ? false : true);
    };

    return (
        <FadeTransition>
            <main aria-labelledby="main-content">
                <div className="main-container max-md:mt-10">
                    <div className={clsx(style.login, "container", "mt-10")}>
                        <div className={style.emHeading}>
                            <div className="grid grid-cols-1">
                                <h1>Two-step Authentication</h1>
                                <p>We sent a text message with a 6-digit security code</p>
                                <p>to your registered phone number ending in {lastFourNumber}</p>
                                <p>Enter the code here.</p>

                                {/* <p>We’ve sent you an approval request.</p>
                                <p>Check your registered device to</p>
                                <p>approve sign in.</p> */}
                            </div>
                        </div>
                        <div className="flex" style={{ justifyContent: 'center', marginTop: 20 }}>
                            {codeIsValid === "true" ?
                                <AuthCode
                                    onChange={(e) => { }}
                                    inputClassName="vi__character--valid"
                                    disabled={codeIsValid === "true"}
                                />
                                :
                                <AuthCode
                                    onChange={(e) => {
                                        let updatedCode = '';
                                        e.forEach(char => updatedCode += char);
                                        verificationHandler(updatedCode);
                                    }}
                                    inputClassName={(codeInputIndicator === 0 ? "vi__character" : (codeInputIndicator === 1 ? "vi__character--error" : "vi__character--valid"))}
                                    disabled={codeIsValid === "true"}
                                    ref={authCodeRef}
                                />
                            }
                        </div>

                        <div className="mt-5 flex justify-center">
                            {verifyingCode &&
                                <motion.div
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0 }}>
                                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />
                                </motion.div>
                            }
                        </div>

                        <div >
                            {codeIsValid === "true" && !verifyingCode && (
                                <div className="flex relative justify-center items-start mt-3">
                                    <img
                                        src="assets/success-icon.svg"
                                        className="w-4 h-4 mt-1"
                                        alt="error"
                                        height={5}
                                        width={5}
                                    />
                                    <span className="flex font-medium tracking-wide success-text text-xs mt-1 ml-1">
                                        Your phone number has been successfully verified!
                                    </span>
                                </div>
                            )}
                        </div>
                        <div >
                            {codeResent && (
                                <div className="flex relative justify-center items-start">
                                    <img
                                        src="assets/success-icon.svg"
                                        className="w-4 h-4 mt-1"
                                        alt="error"
                                        height={5}
                                        width={5}
                                    />
                                    <span className="flex font-medium tracking-wide success-text text-xs mt-1 ml-1">
                                        Verification code resent!
                                    </span>
                                </div>
                            )}
                        </div>
                        <div>
                            {codeIsValid === "false" && !verifyingCode && (
                                <div className="flex relative justify-center items-start mt-3">
                                    <img
                                        src="assets/error-icon.svg"
                                        className="w-4 h-4 mt-1"
                                        alt="error"
                                        height={5}
                                        width={5}
                                    />
                                    <span className="flex items-center font-medium tracking-wide error-text text-xs mt-1 ml-1">
                                        Invalid code, please try again
                                    </span>
                                </div>
                            )}

                        </div>
                        {(resendCounter < 3 && invalidCounter < 3 && codeIsValid !== "true") && (
                            <div className="mt-5 text-center">
                                <span>
                                    Didn't receive a code?
                                    <a onClick={() => reSendCode(phoneNumber, "sms")} className="clickText"> Resend Code</a> or
                                    <a onClick={() => reSendCode(phoneNumber, "voice")} className="clickText"> Resend by Voice Call</a>
                                </span>

                            </div>
                        )}
                        <div className="mt-5 flex flex-col-reverse">
                            <label></label>
                            <input
                                id="termsAccepted"
                                type="checkbox"
                                value={rememberDevice ? "true" : "false"}
                                onChange={(e) => setRememberDevice(e.target.checked)}
                                className="accent"
                            />
                            <label htmlFor="termsAccepted">
                                <p className="sub-heading ml-7 -mt-5">
                                    Don't ask again on this device for 60 days
                                </p>
                            </label>
                        </div>
                        <div className="flex relative">
                            <button
                                disabled={!(codeIsValid == "true") || fetchingInfo}
                                onClick={handleClick}
                                className={clsx("primary mt-5 w-full sm:w-28 h-11 px-6", !(codeIsValid == "true") ? style.loginDisabled : "")}>
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    style={{ marginRight: 8 }}
                                    className={clsx(fetchingInfo ? "" : "hidden")}
                                />
                                Sign in
                            </button>
                        </div>
                        <div className="text-center mt-5">
                            <p className="mt-5 mb-3">If you're having difficulty signing in, please call</p>
                            <p className="active">1-866-592-2226</p>
                        </div>
                    </div>
                </div>
            </main>
        </FadeTransition>
    );
}
